import { memo, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import type { FootnoteMap } from '../../types';

export interface FootnotesProps {
  /** Rendered footnote section coming from the pipeline. */
  children?: ReactNode;
  /** Standalone usage: a map of marker to note content. */
  footnotes?: FootnoteMap;
  label?: string;
}

/**
 * Footnote section.
 *
 * Wraps the GFM-generated `section[data-footnotes]` for styling, and can also be
 * driven directly from a map for standalone use.
 */
export const Footnotes = memo(function Footnotes({
  children,
  footnotes,
  label = 'Footnotes',
}: FootnotesProps) {
  const theme = useTheme();
  const entries = footnotes ? Object.entries(footnotes) : [];

  if (!children && entries.length === 0) return null;

  return (
    <Box
      component="section"
      className="footnotes"
      aria-labelledby="footnote-label"
      sx={{
        mt: 6,
        pt: 2,
        fontSize: '0.92rem',
        color: theme.palette.text.secondary,
        '& li:target': { backgroundColor: theme.markdown.searchHit, borderRadius: 1 },
      }}
    >
      <Divider sx={{ mb: 2 }} />
      <Typography
        id="footnote-label"
        component="h2"
        variant="subtitle2"
        sx={{ fontWeight: 700, mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}
      >
        {label}
      </Typography>

      {children ?? (
        <Box component="ol" sx={{ pl: 2.5, m: 0 }}>
          {entries.map(([key, value]) => (
            <Box component="li" key={key} id={`user-content-fn-${key}`} sx={{ mb: 0.75 }}>
              <Box component="span" sx={{ fontWeight: 700, mr: 0.75 }}>
                [{key}]
              </Box>
              {value}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
});

export default Footnotes;
